import { ConvexHttpClient } from 'convex/browser';
import { anyApi } from 'convex/server';
import { mergeSales, summarizeSales } from '../convex/reporting';

export type ReportPage = { summary: any; orders: any[]; isDone: boolean; continueCursor: string; start: number; end: number };
// Only the first detail page is retained while summary pages are reduced.
// The supplied reader must hold one snapshot for the lifetime of this report.
export async function loadCompleteReport(read: (cursor: string | null, detail: boolean) => Promise<ReportPage>, progress?: (count: number) => void, active: () => boolean = () => true) {
  let cursor: string | null = null, summary: any = summarizeSales([]), first: ReportPage | undefined;
  do {
    if (!active()) throw new Error('Report cancelled.');
    const page = await read(cursor, cursor === null);
    if (!active()) throw new Error('Report cancelled.');
    first ??= page;
    summary = mergeSales(summary, page.summary);
    progress?.(summary.paidCount);
    if (page.isDone) break;
    if (!page.continueCursor || page.continueCursor === cursor) throw new Error('Report could not advance. Run the report again.');
    cursor = page.continueCursor;
  } while (true);
  return { ...summary, orders: first!.orders, start: first!.start, end: first!.end, timezone: 'Asia/Jakarta', basis: 'payment_verified_at',
    revenue: summary.grossRevenue, refundTotal: summary.providerRefunds + summary.externalRefunds,
    detailPage: first!, loadDetail: (cursor: string | null) => read(cursor, true) };
}

export async function loadLiveReport(url: string, token: string, range: { from: string; to: string }, normalize: (order: any) => any, progress?: (count: number) => void, active?: () => boolean, readDetail?: (args: any) => Promise<any>) {
 const client = new ConvexHttpClient(url); client.setAuth(token);
 const args = (cursor: string | null, detail: boolean) => ({ ...range, detail, paginationOpts: { numItems: 50, cursor } });
 const view = (page: any) => ({ ...page, orders: page.orders.map(normalize).filter(Boolean) });
 const report = await loadCompleteReport(async (cursor, detail) => {
  try { return view(await client.consistentQuery(anyApi.staff.reportPage, args(cursor, detail))); }
  catch { throw new Error('Report snapshot could not be loaded. Run the report again or choose a shorter date range.'); }
 }, progress, active);
 // Historic snapshots may expire after 30 seconds. Detail navigation reads current
 // records, with a clear UI distinction from the completed point-in-time totals.
 return { ...report, liveDetails: true, loadDetail: async (cursor: string | null) => view(await (readDetail ? readDetail(args(cursor, true)) : client.query(anyApi.staff.reportPage, args(cursor, true)))) };
}
